import type { RunSettings } from '../multiplayer/RunSettings';
import Phaser from 'phaser';
import { GAME_W, GAME_H, WORLD_NAMES, MAX_LEVELS } from '../constants';
import { GameMode } from '../types';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';

// ── Types ────────────────────────────────────────────────────────────────────

export interface VictoryData extends RunSettings {
  score: number;
  level: number;
  timeBonus: number;
  nextLevel: number;
  mode: GameMode;
}

interface Confetto {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  color: number;
  rotation: number;
  rotSpeed: number;
  alpha: number;
}

// ── Scene ────────────────────────────────────────────────────────────────────

export default class VictoryScene extends Phaser.Scene {
  private sceneData!: VictoryData;
  private confetti: Confetto[] = [];
  private confettiGraphics!: Phaser.GameObjects.Graphics;
  private isGameComplete = false;
  private isNewHighScore = false;

  constructor() {
    super({ key: 'VictoryScene' });
  }

  init(data: VictoryData): void {
    this.sceneData = {
      playerCount: data?.playerCount, playerNames: data?.playerNames, testStage: data?.testStage,
      score: data?.score ?? 0,
      level: data?.level ?? 1,
      timeBonus: data?.timeBonus ?? 0,
      nextLevel: data?.nextLevel ?? 2,
      mode: data?.mode ?? GameMode.CAMPAIGN,
    };
    this.isGameComplete = this.sceneData.nextLevel > MAX_LEVELS;
    this.isNewHighScore = this.sceneData.score > saveSystem.highScore;

    // Persist progress
    if (this.sceneData.mode !== GameMode.ARCADE) saveSystem.unlockLevel(this.sceneData.nextLevel);
    if (this.isNewHighScore) saveSystem.updateHighScore(this.sceneData.score);
  }

  create(): void {
    audioSystem.play(this.isGameComplete ? 'victory' : 'level_complete');

    this.buildBackground();
    this.buildTitle();
    this.buildWorldBanner();
    this.buildStars();
    this.buildScorePanel();
    this.buildButtons();
    this.spawnConfetti(this.isGameComplete ? 80 : 50);

    this.cameras.main.setAlpha(0);
    this.tweens.add({ targets: this.cameras.main, alpha: 1, duration: 400, ease: 'Linear' });
  }

  update(): void {
    this.updateConfetti();
  }

  // ── Background ────────────────────────────────────────────────────────────

  private buildBackground(): void {
    // Dark background
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x050510).setDepth(0);

    // Stars field
    const stars = this.add.graphics().setDepth(1);
    for (let i = 0; i < 120; i++) {
      const x = Phaser.Math.Between(0, GAME_W);
      const y = Phaser.Math.Between(0, GAME_H);
      const r = Phaser.Math.FloatBetween(0.5, 2);
      const alpha = Phaser.Math.FloatBetween(0.2, 0.8);
      stars.fillStyle(0xffffff, alpha);
      stars.fillCircle(x, y, r);
    }

    // Central glow
    const glow = this.add.graphics().setDepth(1);
    if (this.isGameComplete) {
      glow.fillStyle(0x441100, 0.35);
      glow.fillCircle(GAME_W / 2, GAME_H / 2, 440);
      glow.fillStyle(0x220044, 0.25);
      glow.fillCircle(GAME_W / 2, GAME_H / 2, 340);
    } else {
      glow.fillStyle(0x002244, 0.4);
      glow.fillCircle(GAME_W / 2, GAME_H / 2 - 40, 380);
    }
  }

  // ── Title ─────────────────────────────────────────────────────────────────

  private buildTitle(): void {
    const cx = GAME_W / 2;

    if (this.isGameComplete) {
      this.buildGameCompleteTitle(cx);
    } else {
      this.buildLevelCompleteTitle(cx);
    }
  }

  private buildLevelCompleteTitle(cx: number): void {
    // Shadow
    this.add.text(cx + 3, 73, 'NIVEL COMPLET!', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '66px',
      color: '#003300',
    }).setOrigin(0.5).setDepth(3);

    // Main
    const title = this.add.text(cx, 70, 'NIVEL COMPLET!', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '66px',
      color: '#00ff88',
      stroke: '#005533',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 0, color: '#00ff88', blur: 20, fill: true },
    }).setOrigin(0.5).setDepth(4);

    this.tweens.add({
      targets: title,
      scaleX: 1.04, scaleY: 1.04,
      duration: 800, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private buildGameCompleteTitle(cx: number): void {
    // Congratulations panel
    const panel = this.add.graphics().setDepth(3);
    panel.fillStyle(0x110022, 0.9);
    panel.fillRoundedRect(cx - 380, 30, 760, 100, 16);
    panel.lineStyle(3, 0xffaa00, 0.9);
    panel.strokeRoundedRect(cx - 380, 30, 760, 100, 16);

    // Main text line 1
    const t1 = this.add.text(cx, 60, 'FELICITĂRI!', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '52px',
      color: '#ffff00',
      stroke: '#554400',
      strokeThickness: 6,
      shadow: { offsetX: 0, offsetY: 0, color: '#ffff00', blur: 22, fill: true },
    }).setOrigin(0.5).setDepth(4);

    // Line 2
    const t2 = this.add.text(cx, 108, 'AI TERMINAT JOCUL!', {
      fontFamily: 'Arial Black, Arial',
      fontSize: '30px',
      color: '#ffaa00',
      stroke: '#442200',
      strokeThickness: 4,
    }).setOrigin(0.5).setDepth(4);

    this.tweens.add({
      targets: [t1, t2],
      scaleX: 1.04, scaleY: 1.04,
      duration: 900, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  // ── World Banner ──────────────────────────────────────────────────────────

  private buildWorldBanner(): void {
    const cx = GAME_W / 2;
    const level = this.sceneData.level;
    const worldIndex = Math.floor((level - 1) / 6);
    const worldName = WORLD_NAMES[Math.min(worldIndex, WORLD_NAMES.length - 1)] ?? 'Unknown';
    const levelInWorld = ((level - 1) % 6) + 1;

    if (!this.isGameComplete) {
      this.add.text(cx, 152, `NIVEL ${level}  —  ${worldName}  —  Etapa ${levelInWorld}/6`, {
        fontFamily: 'Arial',
        fontSize: '15px',
        color: '#4488aa',
        letterSpacing: 2,
      }).setOrigin(0.5).setDepth(4);
    }
  }

  // ── Stars ─────────────────────────────────────────────────────────────────

  private buildStars(): void {
    const cx = GAME_W / 2;
    const baseY = this.isGameComplete ? 160 : 180;
    const stars = this.calcStars();

    // Background strip
    const strip = this.add.graphics().setDepth(4);
    strip.fillStyle(0x0a0a20, 0.7);
    strip.fillRoundedRect(cx - 160, baseY - 10, 320, 80, 12);

    const starPositions = [cx - 90, cx, cx + 90];
    const starColors = [0xffff00, 0xffff00, 0xffff00];
    const offColor = 0x333344;

    starPositions.forEach((x, i) => {
      const earned = i < stars;
      const color = earned ? starColors[i] : offColor;
      this.drawStar(x, baseY + 28, 28, 12, color, earned);
    });

    // Star count text
    this.add.text(cx, baseY + 64, `${stars} / 3 stele`, {
      fontFamily: 'Arial',
      fontSize: '13px',
      color: stars === 3 ? '#ffff00' : '#778899',
    }).setOrigin(0.5).setDepth(5);
  }

  private calcStars(): number {
    const timeBonus = this.sceneData.timeBonus;
    if (timeBonus > 2000) return 3;
    if (timeBonus > 800) return 2;
    return 1;
  }

  private drawStar(cx: number, cy: number, outerR: number, innerR: number, color: number, glow: boolean): void {
    const g = this.add.graphics().setDepth(5);
    const points: { x: number; y: number }[] = [];
    const spikes = 5;

    for (let i = 0; i < spikes * 2; i++) {
      const angle = (i * Math.PI) / spikes - Math.PI / 2;
      const r = i % 2 === 0 ? outerR : innerR;
      points.push({ x: cx + Math.cos(angle) * r, y: cy + Math.sin(angle) * r });
    }

    if (glow) {
      // Glow layer
      g.fillStyle(color, 0.25);
      g.fillPoints(points.map(p => ({ x: p.x, y: p.y })), true, true);
    }

    g.fillStyle(color, 1);
    g.fillPoints(points, true, true);
    g.lineStyle(1, glow ? 0xffffff : 0x222233, glow ? 0.5 : 0.3);
    g.strokePoints(points, true);

    if (glow) {
      // Sparkle
      this.tweens.add({
        targets: g,
        scaleX: 1.15, scaleY: 1.15,
        duration: 700, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut',
        delay: Phaser.Math.Between(0, 300),
      });
    }
  }

  // ── Score Panel ───────────────────────────────────────────────────────────

  private buildScorePanel(): void {
    const cx = GAME_W / 2;
    const panelW = 440;
    const panelH = this.isGameComplete ? 200 : 180;
    const panelY = this.isGameComplete ? 270 : 280;

    const panel = this.add.graphics().setDepth(4);
    panel.fillStyle(0x080820, 0.93);
    panel.fillRoundedRect(cx - panelW / 2, panelY, panelW, panelH, 14);
    panel.lineStyle(2, 0x00ff88, 0.5);
    panel.strokeRoundedRect(cx - panelW / 2, panelY, panelW, panelH, 14);

    // Score row
    this.add.text(cx - 90, panelY + 18, 'SCOR', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#446644',
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(5);

    this.add.text(cx - 90, panelY + 42, this.sceneData.score.toLocaleString(), {
      fontFamily: 'Arial Black, Arial',
      fontSize: '34px',
      color: '#ffaa00',
      stroke: '#442200',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(5);

    // Time bonus row
    this.add.text(cx + 90, panelY + 18, 'BONUS TIMP', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#446644',
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(5);

    const timeBonusText = this.add.text(cx + 90, panelY + 42, `+${this.sceneData.timeBonus.toLocaleString()}`, {
      fontFamily: 'Arial Black, Arial',
      fontSize: '34px',
      color: '#00ffcc',
      stroke: '#003322',
      strokeThickness: 3,
    }).setOrigin(0.5).setDepth(5);

    // Bounce in time bonus
    this.tweens.add({
      targets: timeBonusText,
      scaleX: 1.2, scaleY: 1.2,
      duration: 300, yoyo: true,
      delay: 500,
      ease: 'Back.easeOut',
    });

    // Divider
    const divider = this.add.graphics().setDepth(5);
    divider.lineStyle(1, 0x224422, 0.6);
    divider.lineBetween(cx - panelW / 2 + 20, panelY + 86, cx + panelW / 2 - 20, panelY + 86);

    // Total score
    const totalScore = this.sceneData.score + this.sceneData.timeBonus;
    this.add.text(cx, panelY + 100, 'SCOR TOTAL', {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#446644',
      letterSpacing: 2,
    }).setOrigin(0.5).setDepth(5);

    this.add.text(cx, panelY + 126, totalScore.toLocaleString(), {
      fontFamily: 'Arial Black, Arial',
      fontSize: '42px',
      color: '#ffffff',
      stroke: '#004422',
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 0, color: '#00ff88', blur: 12, fill: true },
    }).setOrigin(0.5).setDepth(5);

    // New high score
    if (this.isNewHighScore) {
      const badge = this.add.text(cx, panelY + panelH - 22, '★  NOU RECORD PERSONAL!  ★', {
        fontFamily: 'Arial Black, Arial',
        fontSize: '14px',
        color: '#ffff00',
        stroke: '#554400',
        strokeThickness: 2,
      }).setOrigin(0.5).setDepth(5);

      this.tweens.add({
        targets: badge,
        alpha: 0.6,
        duration: 500, yoyo: true, repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  // ── Buttons ───────────────────────────────────────────────────────────────

  private buildButtons(): void {
    const cx = GAME_W / 2;
    const btnY = this.isGameComplete ? 512 : 500;
    const btnH = 54;
    const btnW = 260;

    if (this.isGameComplete) {
      // Only main menu for completed game
      this.buildButton(cx, btnY, btnW + 40, btnH, '🏆  MENIU PRINCIPAL', 0xffaa00, () => this.goToMainMenu());
    } else {
      // Next level + main menu
      this.buildButton(cx - 148, btnY, btnW, btnH, this.sceneData.mode === GameMode.ARCADE ? '▶  O NOUĂ RUNDĂ' : '▶  NIVELUL URMĂTOR', 0x00ff88, () => this.nextLevel(), 0);
      this.buildButton(cx + 148, btnY, btnW, btnH, '⌂  MENIU PRINCIPAL', 0xff6644, () => this.goToMainMenu(), 80);
    }

    // Key hints
    const hint = this.isGameComplete
      ? 'ENTER — Meniu principal'
      : this.sceneData.mode === GameMode.ARCADE ? 'ENTER / N — O nouă rundă   |   ESC — Meniu principal' : 'ENTER / N — Nivelul următor   |   ESC — Meniu principal';

    this.add.text(cx, btnY + 44, hint, {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#334455',
    }).setOrigin(0.5).setDepth(8);

    // Keyboard
    if (!this.isGameComplete) {
      this.input.keyboard!.on('keydown-N', () => this.nextLevel());
      this.input.keyboard!.on('keydown-ENTER', () => this.nextLevel());
    } else {
      this.input.keyboard!.on('keydown-ENTER', () => this.goToMainMenu());
    }
    this.input.keyboard!.on('keydown-ESC', () => this.goToMainMenu());
  }

  private buildButton(
    x: number, y: number,
    w: number, h: number,
    label: string, color: number,
    action: () => void,
    delay = 0
  ): void {
    const container = this.add.container(x, y).setDepth(10);

    const bg = this.add.graphics();
    bg.fillStyle(0x0a0a22, 0.95);
    bg.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    bg.lineStyle(2, color, 0.6);
    bg.strokeRoundedRect(-w / 2, -h / 2, w, h, 10);
    container.add(bg);

    const txt = this.add.text(0, 1, label, {
      fontFamily: 'Arial Black, Arial',
      fontSize: '18px',
      color: Phaser.Display.Color.IntegerToColor(color).rgba,
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);
    container.add(txt);

    const hit = this.add.rectangle(0, 0, w, h, 0xffffff, 0);
    hit.setInteractive({ useHandCursor: true });
    container.add(hit);

    const glowFill = this.add.graphics();
    glowFill.fillStyle(color, 0.12);
    glowFill.fillRoundedRect(-w / 2, -h / 2, w, h, 10);
    glowFill.setAlpha(0);
    container.add(glowFill);

    hit.on('pointerover', () => {
      audioSystem.play('menu_hover');
      glowFill.setAlpha(1);
      this.tweens.add({ targets: container, scaleX: 1.05, scaleY: 1.05, duration: 80, ease: 'Linear' });
    });
    hit.on('pointerout', () => {
      glowFill.setAlpha(0);
      this.tweens.add({ targets: container, scaleX: 1, scaleY: 1, duration: 80, ease: 'Linear' });
    });
    hit.on('pointerdown', () => {
      audioSystem.play('menu_click');
      this.tweens.add({
        targets: container,
        scaleX: 0.94, scaleY: 0.94,
        duration: 60, yoyo: true, ease: 'Linear',
        onComplete: () => action(),
      });
    });

    container.setAlpha(0).setY(y + 40);
    this.tweens.add({
      targets: container,
      y, alpha: 1,
      duration: 300, delay: 600 + delay,
      ease: 'Back.easeOut',
    });
  }

  // ── Confetti ──────────────────────────────────────────────────────────────

  private spawnConfetti(count: number): void {
    this.confettiGraphics = this.add.graphics().setDepth(8);

    const colors = [0xff4444, 0x44ff88, 0x4488ff, 0xffff44, 0xff44ff, 0x44ffff, 0xff8800, 0xffffff];

    for (let i = 0; i < count; i++) {
      this.confetti.push({
        x: Phaser.Math.Between(0, GAME_W),
        y: Phaser.Math.Between(-GAME_H, 0),
        vx: Phaser.Math.FloatBetween(-40, 40),
        vy: Phaser.Math.FloatBetween(40, 120),
        w: Phaser.Math.Between(6, 14),
        h: Phaser.Math.Between(4, 8),
        color: Phaser.Utils.Array.GetRandom(colors) as number,
        rotation: Phaser.Math.FloatBetween(0, Math.PI * 2),
        rotSpeed: Phaser.Math.FloatBetween(-3, 3),
        alpha: Phaser.Math.FloatBetween(0.6, 1),
      });
    }
  }

  private updateConfetti(): void {
    const dt = this.game.loop.delta / 1000;
    this.confettiGraphics.clear();

    for (const c of this.confetti) {
      c.x += c.vx * dt;
      c.y += c.vy * dt;
      c.rotation += c.rotSpeed * dt;
      c.vx += Phaser.Math.FloatBetween(-8, 8) * dt; // drift

      // Wrap vertically
      if (c.y > GAME_H + 20) {
        c.y = -20;
        c.x = Phaser.Math.Between(0, GAME_W);
        c.vy = Phaser.Math.FloatBetween(40, 120);
        c.vx = Phaser.Math.FloatBetween(-40, 40);
      }

      this.confettiGraphics.fillStyle(c.color, c.alpha);

      // Draw rotated rectangle (approximate with a thin rect)
      const cos = Math.cos(c.rotation);
      const sin = Math.sin(c.rotation);
      const hw = c.w / 2;
      const hh = c.h / 2;

      // 4 corners rotated around center
      const corners = [
        { x: -hw, y: -hh },
        { x: hw, y: -hh },
        { x: hw, y: hh },
        { x: -hw, y: hh },
      ].map(pt => ({
        x: c.x + pt.x * cos - pt.y * sin,
        y: c.y + pt.x * sin + pt.y * cos,
      }));

      this.confettiGraphics.fillPoints(corners, true, true);
    }
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  private nextLevel(): void {
    this.cameras.main.fadeOut(280, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('GameScene', {
        playerCount: this.sceneData.playerCount, playerNames: this.sceneData.playerNames, testStage: this.sceneData.testStage,
        level: this.sceneData.mode === GameMode.ARCADE ? 1 : this.sceneData.nextLevel,
        mode: this.sceneData.mode,
        score: this.sceneData.mode === GameMode.ARCADE ? 0 : this.sceneData.score + this.sceneData.timeBonus,
      });
    });
  }

  private goToMainMenu(): void {
    audioSystem.stopMusic();
    this.cameras.main.fadeOut(280, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('MainMenuScene');
    });
  }
}
